import { useQuery, type QueryClient, type Updater } from '@tanstack/react-query'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { FormConfig } from '@omnea-app/packages/universal/formBuilder/form'
import { RequestFormDetailResponse as OpenApiRequestFormDetailResponse } from '@omnea-app/requests/requestsFrontendClient'
import { RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'

export const requestFormQueryKey = 'requestForm'

// Use this for frontend
export type RequestFormDetailResponse = Omit<OpenApiRequestFormDetailResponse, 'form'> & {
  form: Omit<OpenApiRequestFormDetailResponse['form'], 'config'> & { config: FormConfig }
}

export const useGetRequestForm = (requestFormId: string, options?: { refetchInterval?: number; skip?: boolean }) => {
  const { getToken } = useAuthenticatedUser()

  return useQuery({
    queryKey: [requestFormQueryKey, requestFormId],
    enabled: !(options?.skip ?? false),
    refetchInterval: options?.refetchInterval,
    queryFn: async () => {
      const authorization = await getToken()
      const requestsClient = new RequestsHttpClient({ authorization })
      const { data, error } = await requestsClient.GET('/request-form/{requestFormId}', {
        params: { path: { requestFormId } },
      })
      throwIfApiError(error)
      // only for enums
      return data as RequestFormDetailResponse
    },
  })
}

export const updateGetRequestFormCache = (
  queryClient: QueryClient,
  requestFormId: string,
  data: Updater<RequestFormDetailResponse | undefined, RequestFormDetailResponse | undefined>
) => queryClient.setQueryData<RequestFormDetailResponse>([requestFormQueryKey, requestFormId], data)

export const updateGetRequestFormCacheWithUpdatedSubmission = (
  queryClient: QueryClient,
  requestFormId: string,
  {
    submissionId,
    submittedAt,
    user,
    submitter,
    response,
    taskId,
    isCompleted,
  }: {
    submissionId: string
    submittedAt: string
    user: { id: string; firstName: string; lastName: string }
    submitter: NonNullable<RequestFormDetailResponse['form']['latestSubmission']>['submitter']
    response: NonNullable<RequestFormDetailResponse['form']['latestSubmission']>['response']
    taskId: string | null | undefined
    isCompleted: boolean
  }
) =>
  updateGetRequestFormCache(queryClient, requestFormId, (oldData) => {
    if (!oldData) return oldData

    return {
      ...oldData,
      form: {
        ...oldData.form,
        latestSubmission: {
          id: submissionId,
          submittedOn: submittedAt,
          user,
          submitter,
          response,
          detectedLanguage: null,
          translations: [],
          requestStepTask: taskId != null ? { id: taskId, completedOn: isCompleted ? submittedAt : null } : null,
        },
      },
    }
  })
